import React, { useCallback, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Badge,
  Card,
  CardHeader,
  Textarea,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  ArrowDownload24Regular,
} from "@fluentui/react-icons";

import { useProposalApi } from "./useProposalApi";
import type { Proposal } from "./types";
import { STATUS_LABELS, STATUS_COLORS } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    maxWidth: "700px",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingVerticalS,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface ProposalDetailProps {
  proposal: Proposal;
  isAdmin: boolean;
  onBack: () => void;
  onRefresh: (proposal: Proposal) => void;
}

export const ProposalDetail: React.FC<ProposalDetailProps> = ({
  proposal,
  isAdmin,
  onBack,
  onRefresh,
}) => {
  const styles = useStyles();
  const proposalApi = useProposalApi();

  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    setError(null);
    try {
      const updated = await proposalApi.approve(proposal.id);
      onRefresh(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setApproving(false);
    }
  }, [proposalApi, proposal.id, onRefresh]);

  const handleReject = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setRejecting(true);
    setError(null);
    try {
      const updated = await proposalApi.requestChanges(proposal.id, rejectReason);
      onRefresh(updated);
      setRejectDialogOpen(false);
      setRejectReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request changes failed");
    } finally {
      setRejecting(false);
    }
  }, [proposalApi, proposal.id, rejectReason, onRefresh]);

  const handleDownload = useCallback(
    async (artifact: string) => {
      try {
        const url = await proposalApi.download(proposal.id, artifact);
        window.open(url, "_blank");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Download failed");
      }
    },
    [proposalApi, proposal.id],
  );

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const m = proposal.model;

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS }}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack}>
            Back
          </Button>
          <Text size={400} weight="semibold">
            {m.proposal_id || proposal.id.slice(0, 8)}
          </Text>
          <Badge color={STATUS_COLORS[proposal.status]} appearance="filled">
            {STATUS_LABELS[proposal.status]}
          </Badge>
        </div>
      </div>

      {error && (
        <MessageBar intent="error" style={{ margin: tokens.spacingHorizontalM }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {proposal.status === "changes_requested" && proposal.approval.rejection_reason && (
        <MessageBar intent="warning" style={{ margin: tokens.spacingHorizontalM }}>
          <MessageBarBody>
            <strong>Changes requested:</strong> {proposal.approval.rejection_reason}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.content}>
        <Card>
          <CardHeader header={<Text weight="semibold">Proposal Details</Text>} />
          <div style={{ padding: tokens.spacingHorizontalM }}>
            <div className={styles.detailGrid}>
              <Text><strong>Agency:</strong> {m.agency_name}</Text>
              <Text><strong>State:</strong> {m.agency_state}</Text>
              <Text>
                <strong>Contact:</strong>{" "}
                {[
                  m.agency_contact_title,
                  m.agency_contact_first_name,
                  m.agency_contact_last_name,
                ].filter(Boolean).join(" ") || m.agency_contact_name}
              </Text>
              <Text><strong>Address:</strong> {m.agency_address}</Text>
              <Text><strong>Services:</strong> {m.service_types.join(", ")}</Text>
              <Text><strong>Deal Term:</strong> {m.term_months} months</Text>
              <Text><strong>Total:</strong> ${m.total_investment_amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              <Text><strong>Date:</strong> {m.proposal_date}</Text>
              <Text><strong>Author:</strong> {proposal.author_email}</Text>
              <Text><strong>Revision:</strong> {proposal.model_revision}</Text>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader header={<Text weight="semibold">Line Items ({m.line_items.length})</Text>} />
          <div style={{ padding: tokens.spacingHorizontalM, maxHeight: "300px", overflowY: "auto" }}>
            {m.line_items.map((li, idx) => (
              <Text key={idx} style={{ display: "block", marginBottom: tokens.spacingVerticalXS }}>
                {li.quantity}× {li.component}
                {li.description ? ` — ${li.description}` : ""}
              </Text>
            ))}
          </div>
        </Card>

        {proposal.composition?.transmittal?.text_snapshot && (
          <Card>
            <CardHeader header={<Text weight="semibold">Transmittal Letter</Text>} />
            <div style={{ padding: tokens.spacingHorizontalM, maxHeight: "400px", overflowY: "auto" }}>
              <Text style={{ whiteSpace: "pre-wrap" }}>
                {proposal.composition.transmittal.text_snapshot}
              </Text>
            </div>
          </Card>
        )}

        <Card>
          <CardHeader header={<Text weight="semibold">Timeline</Text>} />
          <div style={{ padding: tokens.spacingHorizontalM, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS }}>
            <Text>Created: {formatDate(proposal.created_at)}</Text>
            <Text>Updated: {formatDate(proposal.updated_at)}</Text>
            {proposal.submission.submitted_at && (
              <Text>Submitted: {formatDate(proposal.submission.submitted_at)}</Text>
            )}
            {proposal.approval.approved_at && (
              <Text>Approved: {formatDate(proposal.approval.approved_at)}</Text>
            )}
          </div>
        </Card>

        {/* Download buttons */}
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
          {proposal.artifacts.draft_pdf_blob && (
            <Button
              appearance="outline"
              icon={<ArrowDownload24Regular />}
              onClick={() => handleDownload("draft_pdf")}
              size="small"
            >
              Draft PDF
            </Button>
          )}
          {proposal.artifacts.draft_docx_blob && (
            <Button
              appearance="outline"
              icon={<ArrowDownload24Regular />}
              onClick={() => handleDownload("draft_docx")}
              size="small"
            >
              Draft DOCX
            </Button>
          )}
          {proposal.artifacts.final_pdf_blob && (
            <Button
              appearance="primary"
              icon={<ArrowDownload24Regular />}
              onClick={() => handleDownload("final_pdf")}
              size="small"
            >
              Final PDF
            </Button>
          )}
        </div>
      </div>

      {isAdmin && proposal.status === "submitted" && (
        <div className={styles.footer}>
          <Dialog open={rejectDialogOpen} onOpenChange={(_, d) => setRejectDialogOpen(d.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="outline"
                icon={<Dismiss24Regular />}
              >
                Request Changes
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Request Changes</DialogTitle>
                <DialogContent>
                  <Textarea
                    value={rejectReason}
                    onChange={(_, d) => setRejectReason(d.value)}
                    placeholder="Describe what needs to change…"
                    rows={4}
                    style={{ width: "100%" }}
                  />
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button
                    appearance="primary"
                    onClick={handleReject}
                    disabled={rejecting || !rejectReason.trim()}
                  >
                    {rejecting ? <Spinner size="tiny" /> : "Send"}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          <Button
            appearance="primary"
            icon={<Checkmark24Regular />}
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? <Spinner size="tiny" /> : "Approve"}
          </Button>
        </div>
      )}
    </div>
  );
};
